import { supabase } from '../lib/supabase';

export interface User {
  id: string;
  username: string;
  avatar_url?: string;
  elo_rating: number;
  experience: number;
  level: number;
  badges: string[];
  created_at: string;
  updated_at: string;
  weekly_elo: number;
  last_elo_reset: string;
  is_premium: boolean;
  premium_until?: string;
}

export interface UserStats {
  username: string;
  elo_rating: number;
  progress_history: {
    date: string;
    elo: number;
  }[];
}

const getUserProfile = async (userId: string): Promise<User> => {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .eq('id', userId)
    .single();

  if (error) throw error;
  return data;
};

const updateUserProfile = async (userId: string, updates: Partial<User>): Promise<User> => {
  const { data, error } = await supabase
    .from('users')
    .update(updates)
    .eq('id', userId)
    .select()
    .single();

  if (error) throw error;
  return data;
};

const getUserStats = async (userId: string): Promise<UserStats> => {
  // Get user stats
  const { data: stats, error: statsError } = await supabase
    .from('user_stats')
    .select('username, elo_rating')
    .eq('user_id', userId)
    .single();

  if (statsError) throw statsError;

  // Get progress history
  const { data: history, error: historyError } = await supabase
    .rpc('get_course_progress_history', { p_user_id: userId });

  if (historyError) throw historyError;

  return {
    username: stats.username,
    elo_rating: stats.elo_rating,
    progress_history: history || [],
  };
};

const updateUserProgress = async (userId: string, eloRating: number): Promise<void> => {
  const { error } = await supabase
    .rpc('update_course_progress', {
      p_user_id: userId,
      p_elo_rating: eloRating,
    });

  if (error) throw error;
};

const subscribeToUserChanges = (userId: string, callback: (payload: any) => void) => {
  return supabase
    .channel('public:users')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'users', filter: `id=eq.${userId}` },
      callback
    )
    .subscribe();
};

const uploadUserPicture = async (userId: string, file: File): Promise<string> => {
  try {
    // Validate file type
    if (!file.type.startsWith('image/')) {
      throw new Error('File must be an image');
    }

    // Validate file size (max 6MB as recommended by Supabase)
    const MAX_FILE_SIZE = 6 * 1024 * 1024; // 6MB in bytes
    if (file.size > MAX_FILE_SIZE) {
      throw new Error('File size must be less than 6MB');
    }

    // Generate a unique file name
    const fileExt = file.name.split('.').pop();
    const fileName = `${userId}/${Date.now()}.${fileExt}`;

    // Delete old file if exists
    const { data: oldFiles } = await supabase.storage
      .from('user_picture')
      .list(userId);

    if (oldFiles && oldFiles.length > 0) {
      await Promise.all(
        oldFiles.map(file => 
          supabase.storage
            .from('user_picture')
            .remove([`${userId}/${file.name}`])
        )
      );
    }

    // Upload new file
    const { error: uploadError } = await supabase.storage
      .from('user_picture')
      .upload(fileName, file, {
        upsert: false,
        contentType: file.type
      });

    if (uploadError) throw uploadError;

    // Get the public URL using signed URL
    const { data } = await supabase.storage
      .from('user_picture')
      .createSignedUrl(fileName, 31536000); // URL valid for 1 year

    if (!data?.signedUrl) {
      throw new Error('Failed to generate signed URL');
    }

    return data.signedUrl;
  } catch (error) {
    console.error('Error in uploadUserPicture:', error);
    throw error;
  }
};

export const userService = {
  getUserProfile,
  updateUserProfile,
  getUserStats,
  updateUserProgress,
  subscribeToUserChanges,
  uploadUserPicture,
};
